// src/client/components/WorkspaceList/WorkspaceList.tsx
import {
  activeWorkspaceIdAtom,
  selectedProcessIdAtom,
} from '../../atoms/workspace';
import { useWorkspaceProcessCounts } from '../../hooks/useWorkspaceProcessCounts';
import { useWorkspaces } from '../../hooks/useWorkspaces';
import { WorkspaceCard } from './WorkspaceCard';
import { Text, TextInput, UnstyledButton } from '@mantine/core';
import { useAtom, useSetAtom } from 'jotai';
import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';

type WorkspaceListProps = {
  readonly onWorkspaceSelected?: () => void;
};

export const WorkspaceList = ({ onWorkspaceSelected }: WorkspaceListProps) => {
  const [activeWorkspaceId, setActiveWorkspaceId] = useAtom(
    activeWorkspaceIdAtom,
  );
  const setSelectedProcessId = useSetAtom(selectedProcessIdAtom);

  const [addingWorkspace, setAddingWorkspace] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  const { create, rename, workspaces } = useWorkspaces();
  const processCountByWorkspace = useWorkspaceProcessCounts();

  // Auto-select first workspace if none selected or selected doesn't exist
  useEffect(() => {
    if (workspaces.length === 0) {
      return;
    }

    const exists = workspaces.some((ws) => ws.id === activeWorkspaceId);
    if (!activeWorkspaceId || !exists) {
      setActiveWorkspaceId(workspaces[0].id);
    }
  }, [activeWorkspaceId, setActiveWorkspaceId, workspaces]);

  const handleSelect = (id: string) => {
    setActiveWorkspaceId(id);
    setSelectedProcessId(null);
    onWorkspaceSelected?.();
  };

  const handleAddSubmit = async () => {
    const trimmed = newTitle.trim();
    if (trimmed) {
      await create({ title: trimmed });
      setNewTitle('');
      setAddingWorkspace(false);
    } else {
      setAddingWorkspace(false);
      setNewTitle('');
    }
  };

  return (
    <div className="h-full flex flex-col py-2">
      <Text className="text-[10px] uppercase text-black/40 dark:text-white/40 font-semibold tracking-wider px-1 pr-3 mb-2">
        Workspaces
      </Text>

      <div className="flex-1 overflow-y-auto scrollbar-hidden px-2">
        {workspaces.map((ws) => (
          <WorkspaceCard
            canRename={ws.position !== 0}
            isSelected={ws.id === activeWorkspaceId}
            key={ws.id}
            onRename={(title) => rename(ws.id, title)}
            onSelect={() => handleSelect(ws.id)}
            processCount={processCountByWorkspace.get(ws.id) ?? 0}
            title={ws.title}
          />
        ))}
        {addingWorkspace ? (
          <div className="mt-2 pr-2">
            <TextInput
              autoFocus
              onBlur={handleAddSubmit}
              onChange={(event) => setNewTitle(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleAddSubmit();
                }

                if (event.key === 'Escape') {
                  setAddingWorkspace(false);
                  setNewTitle('');
                }
              }}
              placeholder="Workspace name"
              size="xs"
              value={newTitle}
            />
          </div>
        ) : (
          <UnstyledButton
            className="w-full flex items-center justify-center gap-1 py-2 mt-2 rounded-md text-xs text-black/40 dark:text-white/40 hover:text-black/60 dark:hover:text-white/60 border border-dashed border-black/15 dark:border-white/15 hover:border-black/30 dark:hover:border-white/30 transition-colors"
            onClick={() => setAddingWorkspace(true)}
          >
            <Plus size={14} />
            Add workspace
          </UnstyledButton>
        )}
      </div>
    </div>
  );
};
